import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Seo } from "@/components/Layout";
import { Breadcrumbs, Overline } from "@/components/Bits";
import Exercise from "@/components/Exercise";
import AudioButton from "@/components/AudioButton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Clock, Target, BookOpen, MessageCircle, AlertTriangle, MapPin, ArrowRight, Volume2 } from "lucide-react";

export default function LessonDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [phase, setPhase] = useState("learn"); // learn | practice | done
  const [exIdx, setExIdx] = useState(0);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  useEffect(() => {
    api.get(`/lessons/${slug}`).then(({ data }) => setLesson(data)).catch(() => nav("/cursos"));
  }, [slug, nav]);

  const onResult = async (r) => {
    setScore((s) => ({ correct: s.correct + (r.correct ? 1 : 0), total: s.total + 1 }));
    if (user) { try { await api.post("/progress/activity", { lesson_slug: slug, ...r }); } catch (e) { console.error("No se pudo guardar la actividad:", e); } }
  };
  const onNext = () => {
    if (exIdx < lesson.activities.length - 1) setExIdx(exIdx + 1);
    else {
      setPhase("done");
      if (user) api.post("/progress/complete-lesson", { lesson_slug: slug }).catch(() => {});
    }
  };

  if (!lesson) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Cargando lección...</div>;

  return (
    <>
      <Seo title={lesson.title} description={lesson.objective} />
      <div className="border-b border-border bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
          <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Cursos", to: "/cursos" }, { label: lesson.title }]} />
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
            <span className="px-2 py-0.5 rounded-full bg-secondary">{lesson.level}</span>
            <span className="flex items-center gap-1"><Clock size={12} />{lesson.duration_min} min</span>
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{lesson.title}</h1>
          <p className="mt-3 text-muted-foreground flex items-start gap-2"><Target size={16} className="mt-1 shrink-0 text-primary" />{lesson.objective}</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        {phase === "learn" && (
          <div className="space-y-10">
            <section>
              <Overline>Vocabulario principal</Overline>
              <div className="grid sm:grid-cols-2 gap-2">
                {lesson.vocabulary.map((v, i) => (
                  <div key={i} className="rounded-md border border-border bg-card p-4">
                    <p className="font-display font-semibold flex items-center gap-1">{v.de}<AudioButton text={v.de} /></p>
                    <p className="text-sm text-muted-foreground">{v.es}</p>
                    {v.note && <p className="text-xs text-muted-foreground mt-1 italic">{v.note}</p>}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <Overline>Explicación</Overline>
              <p className="leading-relaxed">{lesson.explanation}</p>
            </section>

            <section>
              <Overline>Ejemplos</Overline>
              <div className="space-y-2">
                {lesson.examples.map((e, i) => (
                  <div key={i} className="rounded-md bg-secondary p-4 flex items-start gap-3">
                    <AudioButton text={e.de} />
                    <div><p className="font-medium">{e.de}</p><p className="text-sm text-muted-foreground">{e.es}</p></div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <Overline>Diálogo</Overline>
              <div className="rounded-lg border border-border bg-card p-5 space-y-3">
                {lesson.dialogue.map((d, i) => (
                  <div key={i} className={`flex ${d.speaker === "A" ? "" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-lg px-4 py-2.5 ${d.speaker === "A" ? "bg-secondary" : "bg-primary/10"}`}>
                      <p className="font-medium text-sm flex items-center gap-1">{d.de}<AudioButton text={d.de} size={14} /></p><p className="text-xs text-muted-foreground">{d.es}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {lesson.swiss_note && (
              <section className="rounded-lg border border-accent/40 bg-accent/5 p-5">
                <div className="flex items-center gap-2 mb-2"><MapPin size={16} className="text-accent" /><span className="font-display font-semibold text-sm">Diferencia suiza</span></div>
                <p className="text-sm text-muted-foreground">{lesson.swiss_note}</p>
              </section>
            )}

            <section className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-5">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-amber-600" /><span className="font-display font-semibold text-sm">Errores frecuentes de hispanohablantes</span></div>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">{lesson.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </section>

            <section>
              <Overline>Resumen</Overline>
              <p className="leading-relaxed">{lesson.summary}</p>
            </section>

            <Button size="lg" className="w-full rounded-full" onClick={() => setPhase("practice")} data-testid="start-practice">
              Ir a los ejercicios <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        )}

        {phase === "practice" && (
          <div>
            <Progress value={(exIdx / lesson.activities.length) * 100} className="mb-6" />
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Ejercicio {exIdx + 1} de {lesson.activities.length}</p>
            <Exercise key={exIdx} activity={lesson.activities[exIdx]} index={exIdx} onResult={onResult} onNext={onNext} isLast={exIdx === lesson.activities.length - 1} />
          </div>
        )}

        {phase === "done" && (
          <div className="text-center py-8" data-testid="lesson-done">
            <div className="inline-flex h-16 w-16 rounded-full bg-primary/10 items-center justify-center mb-4"><BookOpen className="text-primary" size={28} /></div>
            <h2 className="font-display font-black text-3xl">¡Lección completada!</h2>
            <p className="text-muted-foreground mt-2">Has acertado {score.correct} de {score.total} ejercicios.</p>
            {lesson.next_lesson ? (
              <Link to={`/lecciones/${lesson.next_lesson.slug}`}><Button className="mt-6 rounded-full" onClick={() => { setPhase("learn"); setExIdx(0); setScore({ correct: 0, total: 0 }); }}>
                Siguiente: {lesson.next_lesson.title} <ArrowRight size={16} className="ml-1" /></Button></Link>
            ) : (
              <Link to="/cursos"><Button className="mt-6 rounded-full">Volver al curso</Button></Link>
            )}
            {!user && <p className="mt-6 text-sm text-muted-foreground">Crea una cuenta gratis para guardar tu progreso. <Link to="/registro" className="text-primary underline">Registrarme</Link></p>}
          </div>
        )}
      </div>
    </>
  );
}
